/**
 * Generated by orval v8.23.0 🍺
 * Do not edit manually.
 * Api
 * Unified Inbox API
 * OpenAPI spec version: 0.1.0
 */
export interface HealthStatus {
  status: string;
}

export type AuthStatusGmail = {
  connected: boolean;
  /** @nullable */
  email: string | null;
  needsReconnect: boolean;
  /** Whether the current connection's OAuth grant includes calendar.events. False for connections made before Calendar sync existed — reconnecting re-consents with the wider scope. */
  calendarScopeGranted: boolean;
  /** Whether the current connection's OAuth grant includes tasks.readonly. False for connections made before Google Tasks sync existed — reconnecting re-consents with the wider scope. */
  tasksScopeGranted: boolean;
};

export type AuthStatusSlack = {
  connected: boolean;
  /** @nullable */
  workspaceName: string | null;
  needsReconnect: boolean;
};

export type AuthStatusOutlook = {
  connected: boolean;
  /** @nullable */
  email: string | null;
  needsReconnect: boolean;
  /** Whether the current connection's OAuth grant includes Calendars.ReadWrite. False for connections made before Outlook Calendar support existed — reconnecting re-consents with the wider scope. */
  outlookCalendarScopeGranted: boolean;
  /** Whether the current connection's OAuth grant includes Chat.Read. False for connections made before Teams chat support existed — reconnecting re-consents with the wider scope. */
  outlookTeamsScopeGranted: boolean;
};

export type AuthStatusJira = {
  connected: boolean;
  /** @nullable */
  email: string | null;
  needsReconnect: boolean;
};

export type AuthStatusAdo = {
  connected: boolean;
  /** @nullable */
  organizationName: string | null;
  needsReconnect: boolean;
};

export interface AuthStatus {
  gmail: AuthStatusGmail;
  slack: AuthStatusSlack;
  outlook: AuthStatusOutlook;
  jira: AuthStatusJira;
  ado: AuthStatusAdo;
}

/**
 * Canonical list of every place a message, task, or folder can come from — the real third-party providers plus "native" for first-party chat channels that have no external account behind them. This is the single source of truth for source identity; frontend and backend both generate their types from here instead of keeping their own lists in sync by hand.
 */
export type SourceId = typeof SourceId[keyof typeof SourceId];


export const SourceId = {
  gmail: 'gmail',
  outlook: 'outlook',
  slack: 'slack',
  teams: 'teams',
  jira: 'jira',
  google_tasks: 'google_tasks',
  native: 'native',
} as const;

/**
 * @nullable
 */
export type UnifiedMessageAiPriority = typeof UnifiedMessageAiPriority[keyof typeof UnifiedMessageAiPriority] | null;


export const UnifiedMessageAiPriority = {
  high: 'high',
  medium: 'medium',
  low: 'low',
} as const;

/**
 * The response already sent for this invite, if any — lets the UI show what was previously chosen instead of the plain Accept/Maybe/Decline buttons again.
 * @nullable
 */
export type UnifiedMessageMeetingInviteResponse = typeof UnifiedMessageMeetingInviteResponse[keyof typeof UnifiedMessageMeetingInviteResponse] | null;


export const UnifiedMessageMeetingInviteResponse = {
  accepted: 'accepted',
  declined: 'declined',
  tentative: 'tentative',
} as const;

export interface MessageAttachment {
  /** The provider's own attachment id — passed back to the download route. */
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  /** Set for an inline signature/logo image referenced by cid: in the message body (already rewritten to this attachment's own download URL there) — omit from any attachment-list UI, since it's already shown inline rather than being a separate downloadable file. Absent (not false) on a regular attachment. */
  inline?: boolean;
}

/**
 * A reaction normalized across providers. `key` is the provider's own shortcode/reactionType ("thumbsup" on Slack, "like" on Teams).
 */
export interface MessageReaction {
  key: string;
  count: number;
}

export interface UnifiedMessage {
  id: string;
  source: SourceId;
  fromName: string;
  /** @nullable */
  fromEmail: string | null;
  /** @nullable */
  subject: string | null;
  /** @nullable */
  channel: string | null;
  snippet: string;
  /** @nullable */
  body: string | null;
  timestamp: string;
  isRead: boolean;
  isPinned: boolean;
  /** @nullable */
  snoozedUntil: string | null;
  /** @nullable */
  archivedAt: string | null;
  /**
     * Soft-delete timestamp — set instead of a hard row delete so the real Gmail/Outlook trash push stays locally recoverable via the Trash view / POST /messages/{id}/restore. Null when not deleted.
     * @nullable
     */
  deletedAt: string | null;
  /**
     * Computed grouping key, e.g. "domain:acme.com" or "slack_channel:general". Null when no folder could be determined for this message.
     * @nullable
     */
  folderKey: string | null;
  labels: string[];
  /** @nullable */
  aiPriority: UnifiedMessageAiPriority;
  /** @nullable */
  aiSummary: string | null;
  isMeetingInvite: boolean;
  /**
     * The response already sent for this invite, if any — lets the UI show what was previously chosen instead of the plain Accept/Maybe/Decline buttons again.
     * @nullable
     */
  meetingInviteResponse: UnifiedMessageMeetingInviteResponse;
  /** Metadata only — the file bytes are never stored locally. Fetch them on demand via GET /messages/{id}/attachments/{attachmentId}, which proxies live from Gmail/Outlook. */
  attachments: MessageAttachment[];
  /**
     * Set for a Teams 1:1/group chat message — needed to reply. Null for channel messages and every other source.
     * @nullable
     */
  teamsChatId: string | null;
  /**
     * Set together with teamsChannelId for a Teams channel message. Null for chat messages and every other source.
     * @nullable
     */
  teamsTeamId: string | null;
  /**
     * Set together with teamsTeamId for a Teams channel message. Null for chat messages and every other source.
     * @nullable
     */
  teamsChannelId: string | null;
  /**
     * The sender's Slack (Uxxxx) or Teams (AAD guid) user id — keys the presence dot and proxied avatar. Null for email/task rows.
     * @nullable
     */
  fromProviderUserId?: string | null;
  /** Normalized reactions for a Slack/Teams message. Empty for other sources. */
  reactions?: MessageReaction[];
  /** Display names this message @-mentions. Empty for other sources. */
  mentions?: string[];
  /**
     * Teams message importance ("high" | "urgent"); null for normal importance and every other source.
     * @nullable
     */
  importance?: string | null;
}

export interface MessageUpdate {
  isRead?: boolean;
  isPinned?: boolean;
  /** @nullable */
  snoozedUntil?: string | null;
  /** @nullable */
  archivedAt?: string | null;
  labels?: string[];
}

export type FolderKind = typeof FolderKind[keyof typeof FolderKind];


export const FolderKind = {
  domain: 'domain',
  slack_channel: 'slack_channel',
  sent: 'sent',
  list: 'list',
  provider_folder: 'provider_folder',
  teams_conversation: 'teams_conversation',
  native_channel: 'native_channel',
} as const;

/**
 * Which provider presenceUserId belongs to. Null when presenceUserId is null.
 * @nullable
 */
export type FolderPresenceSource = typeof FolderPresenceSource[keyof typeof FolderPresenceSource] | null;


export const FolderPresenceSource = {
  slack: 'slack',
  teams: 'teams',
} as const;

export interface Folder {
  key: string;
  kind: FolderKind;
  label: string;
  /** True when `label` is a user-set rename rather than the auto-computed domain/channel name. */
  isCustomLabel: boolean;
  messageCount: number;
  unreadCount: number;
  latestTimestamp: string;
  /**
     * An emoji, only meaningful when kind is "list".
     * @nullable
     */
  icon: string | null;
  /**
     * Sync-owned origin — "jira"/"google_tasks" when kind is "list", "gmail"/"outlook" when kind is "provider_folder". Null for native lists and every other kind.
     * @nullable
     */
  source: string | null;
  /**
     * A palette key (see TaskTag.color), only meaningful when kind is "list". Null means the default neutral color.
     * @nullable
     */
  color: string | null;
  /**
     * The parent folder's own key, only meaningful when kind is "provider_folder" (mirrors the account's real Gmail label tree / Outlook mail folder tree). Null for a top-level provider folder and for every other kind, which are always flat.
     * @nullable
     */
  parentKey: string | null;
  /**
     * For a 1:1/group Slack or Teams chat, the provider user id of the most recent non-"You" sender — used to show that person's presence dot in the nav. Null for channels, non-chat folders, and chats with no resolvable other person.
     * @nullable
     */
  presenceUserId?: string | null;
  /**
     * Which provider presenceUserId belongs to. Null when presenceUserId is null.
     * @nullable
     */
  presenceSource?: FolderPresenceSource;
}

export interface DeleteFolderRequest {
  key: string;
}

export interface RenameFolderRequest {
  key: string;
  /**
     * New display label. Pass null (or an empty string) to clear the custom label and revert to the auto-computed one.
     * @nullable
     */
  label: string | null;
}

export interface FileMessageToFolderRequest {
  /** A provider_folder-kind Folder.key, e.g. "provider_folder:gmail:Label_12". */
  folderKey: string;
}

export interface ReorderFoldersRequest {
  /** Folder keys in the desired display order. Each gets a sequential sortOrder assigned by its index. */
  keys: string[];
}

export interface ReorderTasksRequest {
  /** Task ids in the desired display order. Each gets a sequential position assigned by its index. */
  ids: number[];
}

export interface DocSummary {
  id: number;
  title: string;
  /**
     * The task this doc is associated with, if any. A task can have many docs; a doc points at at most one task.
     * @nullable
     */
  taskId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface Doc {
  id: number;
  title: string;
  /** Opaque Tiptap JSON document. Null means an empty doc. */
  content: unknown | null;
  /**
     * The task this doc is associated with, if any. A task can have many docs; a doc points at at most one task.
     * @nullable
     */
  taskId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDocRequest {
  /** Defaults to an empty string if omitted. */
  title?: string;
  /** Associate the new doc with a task right away — used by "New doc" from within a task's detail view. */
  taskId?: number;
}

export interface UpdateDocRequest {
  title?: string;
  /** Opaque Tiptap JSON document. */
  content?: unknown | null;
  /**
     * Set or clear the doc's associated task.
     * @nullable
     */
  taskId?: number | null;
}

export interface List {
  id: number;
  name: string;
  /**
     * A custom emoji override. Null means the standard list icon is used.
     * @nullable
     */
  icon: string | null;
  /**
     * Sync-owned origin, e.g. "jira". Null for native, user-created lists.
     * @nullable
     */
  source: string | null;
  /**
     * Identity in the source system (e.g. a Jira project key). Null for native lists.
     * @nullable
     */
  externalId: string | null;
  /**
     * A palette key (see TaskTag.color). Null means the default neutral color.
     * @nullable
     */
  color: string | null;
  createdAt: string;
}

export interface CreateListRequest {
  name: string;
  /** A custom emoji override. Omit to use the standard list icon. */
  icon?: string;
  /** A palette key (see TaskTag.color). Omit to use the default neutral color. */
  color?: string;
}

export interface ChatChannel {
  id: number;
  name: string;
  createdAt: string;
}

export interface CreateChatChannelRequest {
  name: string;
}

export interface NativeReplyRequest {
  /** The channel's own name (native channels have no separate external id). */
  channelId: string;
  text: string;
}

export interface UpdateListRequest {
  /** A palette key (see TaskTag.color). */
  color?: string;
}

export interface Task {
  id: number;
  listId: number;
  title: string;
  /** @nullable */
  description: string | null;
  /** One of "todo" | "in_progress" | "blocked" | "done". */
  status: string;
  /**
     * The source system's own status/state name (e.g. Jira's "In Review", an ADO work item's "Active") — read-only display info alongside `status`. Null for native tasks and Jira/ADO workflows are per-project configurable, so this has no fixed vocabulary.
     * @nullable
     */
  rawStatus: string | null;
  /**
     * Whatever `status` was immediately before it was last set to "done" — restored when un-marking done. Null until the task has been marked done at least once.
     * @nullable
     */
  previousStatus: string | null;
  /**
     * References TaskTag. Null when no priority is set.
     * @nullable
     */
  priorityTagId: number | null;
  /**
     * References the parent Task when this is a subtask. One level deep only.
     * @nullable
     */
  parentTaskId: number | null;
  /**
     * Manual drag order among a list's top-level tasks. Null falls back to computed (due-date) order.
     * @nullable
     */
  position: number | null;
  /** @nullable */
  dueDate: string | null;
  /** @nullable */
  completedAt: string | null;
  /**
     * Sync-owned origin, e.g. "jira". Null for native, user-created tasks.
     * @nullable
     */
  source: string | null;
  /**
     * Identity in the source system (e.g. a Jira issue key). Null for native tasks.
     * @nullable
     */
  externalId: string | null;
  /**
     * Link to the source record (e.g. the Jira issue). Null for native tasks.
     * @nullable
     */
  externalUrl: string | null;
  /** Set when this task's time blocks were created as a recurring series. */
  isRecurring: boolean;
  /**
     * One of "daily" | "weekly" | "monthly". Null unless isRecurring.
     * @nullable
     */
  recurrenceFrequency: string | null;
  /**
     * Repeats every N of recurrenceFrequency's unit. Null unless isRecurring.
     * @nullable
     */
  recurrenceInterval: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskRequest {
  listId: number;
  title: string;
  description?: string;
  /** Defaults to "todo" if omitted — set explicitly so a quick-add under a specific status group lands in that group. */
  status?: string;
  /** @nullable */
  priorityTagId?: number | null;
  /** Usually set at creation for a new subtask — a task can also be re-parented later via drag-and-drop (PATCH /tasks/{id}). */
  parentTaskId?: number;
  /** @nullable */
  dueDate?: string | null;
}

export interface UpdateTaskRequest {
  title?: string;
  /** @nullable */
  description?: string | null;
  status?: string;
  /** @nullable */
  priorityTagId?: number | null;
  /**
     * Set via drag-to-nest. Null removes it from its parent, making it top-level again.
     * @nullable
     */
  parentTaskId?: number | null;
  /** @nullable */
  dueDate?: string | null;
}

export interface TaskTag {
  id: number;
  /** The list this priority level belongs to — priority levels are per-list, not shared across the whole app. */
  listId: number;
  name: string;
  /** A preset palette key (e.g. "red", "orange", "blue"), not arbitrary hex. */
  color: string;
  createdAt: string;
}

export interface CreateTaskTagRequest {
  listId: number;
  name: string;
  color: string;
}

export interface UpdateTaskTagRequest {
  name?: string;
  color?: string;
}

export interface TaskStatus {
  id: number;
  /** The list this custom status belongs to — same per-list scoping as priority tags. */
  listId: number;
  name: string;
  /** A preset palette key (e.g. "red", "orange", "blue"), not arbitrary hex. */
  color: string;
  createdAt: string;
}

export interface CreateTaskStatusRequest {
  listId: number;
  name: string;
  color: string;
}

export interface UpdateTaskStatusRequest {
  name?: string;
  color?: string;
}

/**
 * Opaque bundle of TaskTablePane's own filter/sort/group state — shape owned by the frontend.
 */
export type TaskViewFilters = { [key: string]: unknown };

export interface TaskView {
  id: number;
  /** Null = an "All Tasks" (master) view, scoped across every list. */
  listId: number | null;
  name: string;
  /** Opaque bundle of TaskTablePane's own filter/sort/group state — shape owned by the frontend. */
  filters: TaskViewFilters;
  isDefault: boolean;
  createdAt: string;
}

export type CreateTaskViewRequestFilters = { [key: string]: unknown };

export interface CreateTaskViewRequest {
  listId: number | null;
  name: string;
  filters: CreateTaskViewRequestFilters;
  isDefault?: boolean;
}

export type UpdateTaskViewRequestFilters = { [key: string]: unknown };

export interface UpdateTaskViewRequest {
  name?: string;
  filters?: UpdateTaskViewRequestFilters;
  isDefault?: boolean;
}

export interface PinnedView {
  id: number;
  label: string;
  /** The captured app location, may include a query string (e.g. "/list/42?task=9"). */
  path: string;
  /** Position in the bar, left to right. */
  sortOrder: number;
  /** True once the user has renamed this tab by hand — auto-naming stops. While false, the client re-derives the label from the route on render, so renaming the underlying list or folder renames the tab too; `label` is then the last derived name, kept as the fallback for a route that no longer resolves. */
  isCustomLabel: boolean;
}

export interface CreatePinnedViewRequest {
  label: string;
  /** App-relative path, always starting with "/", optionally with a query string. Validated on write — it is fed back into client-side navigation, so an absolute or "javascript:" value is rejected rather than stored. */
  path: string;
  /** Pass true when the label came from the user rather than from the route. */
  isCustomLabel?: boolean;
}

export interface UpdatePinnedViewRequest {
  /** The tab's new name. Null clears the user's own name and returns the tab to auto-naming from its route; the stored label is left in place as the fallback. */
  label: string | null;
}

export interface ReorderPinnedViewsRequest {
  /** Every pinned view id in the desired left-to-right order. */
  ids: number[];
}

/**
 * One choice in a "select"-type field — only meaningful for that type.
 */
export interface TaskFieldOption {
  name: string;
  /** A preset palette key (e.g. "red", "blue"), not arbitrary hex. */
  color: string;
}

export type TaskFieldType = typeof TaskFieldType[keyof typeof TaskFieldType];


export const TaskFieldType = {
  date: 'date',
  text: 'text',
  textarea: 'textarea',
  select: 'select',
} as const;

export interface TaskField {
  id: number;
  /** The list this field was created on. Provenance only — use listIds to decide where the field applies. */
  listId: number;
  /** Every list this field appears on. One shared definition, many lists — see ADR-0017. */
  listIds: number[];
  name: string;
  type: TaskFieldType;
  /** Only set for type "select". Null for every other type. */
  options?: TaskFieldOption[] | null;
  createdAt: string;
}

export type CreateTaskFieldRequestType = typeof CreateTaskFieldRequestType[keyof typeof CreateTaskFieldRequestType];


export const CreateTaskFieldRequestType = {
  date: 'date',
  text: 'text',
  textarea: 'textarea',
  select: 'select',
} as const;

export interface CreateTaskFieldRequest {
  listId: number;
  name: string;
  type: CreateTaskFieldRequestType;
  options?: TaskFieldOption[];
}

export type UpdateTaskFieldRequestType = typeof UpdateTaskFieldRequestType[keyof typeof UpdateTaskFieldRequestType];


export const UpdateTaskFieldRequestType = {
  date: 'date',
  text: 'text',
  textarea: 'textarea',
  select: 'select',
} as const;

export interface UpdateTaskFieldRequest {
  name?: string;
  type?: UpdateTaskFieldRequestType;
  options?: TaskFieldOption[] | null;
}

export interface AddTaskFieldToListRequest {
  listId: number;
}

export interface TaskFieldValue {
  id: number;
  taskId: number;
  fieldId: number;
  value: string;
  updatedAt: string;
}

export interface UpsertTaskFieldValueRequest {
  taskId: number;
  fieldId: number;
  value: string;
}

export interface TaskComment {
  id: number;
  taskId: number;
  body: string;
  createdAt: string;
}

export interface CreateTaskCommentRequest {
  body: string;
}

/**
 * One independent working period for a task — a task can have several, e.g. one on Monday morning and another on Wednesday afternoon.
 */
export interface TaskTimeBlock {
  id: number;
  taskId: number;
  startDate: string;
  endDate: string;
  /** When true, the calendar renders this period in the all-day strip regardless of its exact start/end times. */
  isAllDay: boolean;
  createdAt: string;
}

export type CalendarEventSource = typeof CalendarEventSource[keyof typeof CalendarEventSource];


export const CalendarEventSource = {
  google: 'google',
  outlook: 'outlook',
} as const;

export type CalendarEventAttendeesItem = {
  email: string;
  /** @nullable */
  name: string | null;
  responseStatus: string;
};

/**
 * A calendar event — either synced (read-only pull) from Google, or created directly from Assistio for Google or Outlook.
 */
export interface CalendarEvent {
  id: number;
  source: CalendarEventSource;
  /** The provider's own event ID. */
  externalId: string;
  calendarId: string;
  title: string;
  /** @nullable */
  description: string | null;
  /** @nullable */
  location: string | null;
  startDate: string;
  endDate: string;
  isAllDay: boolean;
  /**
     * Link to open this event on the provider's own calendar.
     * @nullable
     */
  htmlLink: string | null;
  /**
     * Video-call join link (Google Meet or third-party conferencing), when the event has one.
     * @nullable
     */
  meetingLink: string | null;
  attendees: CalendarEventAttendeesItem[];
  createdAt: string;
}

export type CreateMeetingRequestProvider = typeof CreateMeetingRequestProvider[keyof typeof CreateMeetingRequestProvider];


export const CreateMeetingRequestProvider = {
  google: 'google',
  outlook: 'outlook',
} as const;

export interface CreateMeetingRequest {
  provider: CreateMeetingRequestProvider;
  title: string;
  start: string;
  end: string;
  isAllDay?: boolean;
  description?: string;
  location?: string;
  attendees?: string[];
  /** Auto-generate a Google Meet or Microsoft Teams link for this meeting, depending on provider. */
  addVideoLink?: boolean;
}

export type TaskTimeBlockRecurrenceFrequency = typeof TaskTimeBlockRecurrenceFrequency[keyof typeof TaskTimeBlockRecurrenceFrequency];


export const TaskTimeBlockRecurrenceFrequency = {
  daily: 'daily',
  weekly: 'weekly',
  monthly: 'monthly',
} as const;

export interface TaskTimeBlockRecurrence {
  frequency: TaskTimeBlockRecurrenceFrequency;
  /** Repeat every N of `frequency`'s unit (e.g. frequency=weekly, interval=2 → every 2 weeks). Minimum 1. */
  interval: number;
  /** Total number of occurrences to create, including this first one. Omit for an indefinite ("forever") series — the server then schedules occurrences out to a fixed future horizon instead of a fixed count. Always capped server-side either way. */
  count?: number;
}

export interface CreateTaskTimeBlockRequest {
  startDate: string;
  endDate: string;
  /** Omit for a normal timed period — defaults to false. */
  isAllDay?: boolean;
  /** Omit for a one-off period. When set, this call creates `count` blocks up front (this one plus `count - 1` more, each offset by `interval` × `frequency` from the last) and marks the task recurring — not a rule expanded later, real rows from the start. */
  recurrence?: TaskTimeBlockRecurrence;
}

export interface UpdateTaskTimeBlockRequest {
  startDate?: string;
  endDate?: string;
  isAllDay?: boolean;
}

/**
 * Per-user recolor of the 4 built-in task statuses (key -> palette color name, e.g. {"todo": "purple"}). The 4 keys stay fixed ("done" drives real logic — stamping completedAt — so it's never user-definable); only their display color is. Null/missing key uses the default color.
 */
export type AppSettingsBuiltInStatusColors = {[key: string]: string} | null;

/**
 * General app-level feature settings — singleton row, this app is single-tenant, so there is exactly one. Distinct from WellbeingSettings, which is specifically the Wellbeing pane's own domain of settings.
 */
export interface AppSettings {
  id: number;
  /** Shows/hides the Emails section in the nav rail. */
  emailEnabled: boolean;
  /** Shows/hides the whole Messages section (Slack, Teams, and native Chat) in the nav rail. */
  messagesEnabled: boolean;
  /** Shows/hides the Tasks section in the nav rail. */
  tasksEnabled: boolean;
  /** Shows/hides the Docs section in the nav rail. */
  docsEnabled: boolean;
  /** Shows/hides the Calendar section in the nav rail. */
  calendarEnabled: boolean;
  /** Shows/hides the Wellbeing section in the nav rail. */
  wellbeingEnabled: boolean;
  /** Shows/hides the Analytics section in the nav rail. */
  analyticsEnabled: boolean;
  /** Finer-grained than messagesEnabled — hides just the native chat Chat sub-group within the Messages section (Slack/Teams stay controlled by messagesEnabled alone). */
  nativeChatEnabled: boolean;
  /** Shows/hides the merged "All Emails" header (cross-account Inbox/ Sent/Drafts/Archive/Trash rows) above each connected account's own Gmail/Outlook folder tree. Worth turning off with a single connected email account, where the merged view just duplicates that one account's own tree. */
  emailsUnifiedViewEnabled: boolean;
  /** Per-user recolor of the 4 built-in task statuses (key -> palette color name, e.g. {"todo": "purple"}). The 4 keys stay fixed ("done" drives real logic — stamping completedAt — so it's never user-definable); only their display color is. Null/missing key uses the default color. */
  builtInStatusColors?: AppSettingsBuiltInStatusColors;
  updatedAt: string;
}

export type UpdateAppSettingsRequestBuiltInStatusColors = {[key: string]: string} | null;

export interface UpdateAppSettingsRequest {
  emailEnabled?: boolean;
  messagesEnabled?: boolean;
  tasksEnabled?: boolean;
  docsEnabled?: boolean;
  calendarEnabled?: boolean;
  wellbeingEnabled?: boolean;
  analyticsEnabled?: boolean;
  nativeChatEnabled?: boolean;
  emailsUnifiedViewEnabled?: boolean;
  builtInStatusColors?: UpdateAppSettingsRequestBuiltInStatusColors;
}

export interface AddTrustedImageSenderRequest {
  email: string;
}

/**
 * Singleton settings row — this app is single-tenant, so there is exactly one.
 */
export interface WellbeingSettings {
  id: number;
  morningCheckinEnabled: boolean;
  morningChecklist: string[];
  shutdownRitualEnabled: boolean;
  shutdownChecklist: string[];
  breakRemindersEnabled: boolean;
  /** 24h "HH:MM" times, local time */
  breakTimes: string[];
  pomodoroEnabled: boolean;
  pomodoroWorkMinutes: number;
  pomodoroBreakMinutes: number;
  pomodoroLongBreakMinutes: number;
  pomodoroSessionsBeforeLongBreak: number;
  /** 0 = Sunday, 1 = Monday. Also drives the calendar view's week-start — moved here so it's set in one place. */
  weekStartsOn: number;
  workingHoursNudgesEnabled: boolean;
  /**
     * [startHour, endHour] per day of week, indexed by Date#getDay() (0 = Sunday) — the same working-hours concept the calendar view uses, shared from here so there's one source of truth.
     * @items.minItems 2
     * @items.maxItems 2
     */
  workHoursByDay: number[][];
  longDayThresholdHours: number;
  /** Shows a small "where am I in the working day" timeline in the nav — left edge is today's configured start hour, right edge is the end hour, with a marker at the current time. */
  workingDayTimelineEnabled: boolean;
  updatedAt: string;
}

export interface UpdateWellbeingSettingsRequest {
  morningCheckinEnabled?: boolean;
  morningChecklist?: string[];
  shutdownRitualEnabled?: boolean;
  shutdownChecklist?: string[];
  breakRemindersEnabled?: boolean;
  breakTimes?: string[];
  pomodoroEnabled?: boolean;
  pomodoroWorkMinutes?: number;
  pomodoroBreakMinutes?: number;
  pomodoroLongBreakMinutes?: number;
  pomodoroSessionsBeforeLongBreak?: number;
  weekStartsOn?: number;
  workingHoursNudgesEnabled?: boolean;
  /**
     * @items.minItems 2
     * @items.maxItems 2
     */
  workHoursByDay?: number[][];
  longDayThresholdHours?: number;
  workingDayTimelineEnabled?: boolean;
}

export interface TaskSummary {
  summary: string;
}

export interface JiraIssue {
  /** Issue key, e.g. "PROJ-123". */
  key: string;
  /** Stable project identity, e.g. "PROJ" — used for routing/filtering. */
  projectKey: string;
  /** Human-readable project name, e.g. "My Team" — used for display. */
  projectName: string;
  summary: string;
  /** Display name of the current workflow status, e.g. "In Progress". */
  status: string;
  /** Jira's coarse status bucket ("new" | "indeterminate" | "done"). */
  statusCategory: string;
  /** @nullable */
  priority: string | null;
  /** Link to open the issue in Jira. */
  url: string;
  updatedAt: string;
  unreadCommentCount: number;
}

/**
 * Everything worth showing in the full-window ticket view — fetched live (not cached) when a ticket is opened.
 */
export type JiraIssueDetail = JiraIssue & ({
  /** @nullable */
  description: string | null;
  /**
     * e.g. "Task", "Bug", "Story".
     * @nullable
     */
  issueType: string | null;
  /** @nullable */
  reporterName: string | null;
  /** @nullable */
  assigneeName: string | null;
  createdAt: string;
  /**
     * Date-only, "YYYY-MM-DD".
     * @nullable
     */
  dueDate: string | null;
  labels: string[];
});

/**
 * A priority option available on the connected Jira site (v1 assumes one shared scheme across all connected projects).
 */
export interface JiraPriority {
  id: string;
  name: string;
}

export interface JiraProject {
  key: string;
  name: string;
}

export interface JiraProjectsResponse {
  projects: JiraProject[];
  /** Null or empty means every project is synced. */
  selectedProjectKeys: string[] | null;
}

export interface AdoProject {
  name: string;
}

export interface AdoProjectsResponse {
  projects: AdoProject[];
  /** Null or empty means every project is synced. */
  selectedProjectKeys: string[] | null;
}

/**
 * Shared by /jira/projects and /ado/projects — same shape, provider-specific meaning of each string (Jira project key vs ADO team project name).
 */
export interface UpdateProjectSelectionRequest {
  /** Empty array means every project. */
  selectedProjectKeys: string[];
}

export interface ConnectJiraRequest {
  /** Jira Cloud site, e.g. "https://yourcompany.atlassian.net". */
  siteUrl: string;
  email: string;
  /** Generated at id.atlassian.com/manage-profile/security/api-tokens. */
  apiToken: string;
}

/**
 * Which connection to re-sync. gmail and outlook run the deep mail re-sync (folder tree, then messages, with SSE progress); the rest run a normal sync pass and report what that provider fetched. One control, one endpoint, whatever the user is looking at.
 */
export type ResyncRequestSource = typeof ResyncRequestSource[keyof typeof ResyncRequestSource];


export const ResyncRequestSource = {
  gmail: 'gmail',
  outlook: 'outlook',
  slack: 'slack',
  teams: 'teams',
  jira: 'jira',
  ado: 'ado',
  google_tasks: 'google_tasks',
} as const;

export interface ResyncRequest {
  /** Which connection to re-sync. gmail and outlook run the deep mail re-sync (folder tree, then messages, with SSE progress); the rest run a normal sync pass and report what that provider fetched. One control, one endpoint, whatever the user is looking at. */
  source: ResyncRequestSource;
}

export interface ResyncResult {
  fetched: number;
}

export interface SyncResult {
  gmailFetched: number;
  slackFetched: number;
  outlookFetched: number;
  teamsFetched?: number;
  jiraFetched: number;
  adoFetched: number;
  calendarFetched: number;
  outlookCalendarFetched?: number;
  googleTasksFetched?: number;
  total: number;
  gmailNeedsReconnect: boolean;
  outlookNeedsReconnect: boolean;
  teamsNeedsReconnect?: boolean;
  jiraNeedsReconnect: boolean;
  adoNeedsReconnect: boolean;
  calendarNeedsReconnect: boolean;
  outlookCalendarNeedsReconnect?: boolean;
  googleTasksNeedsReconnect?: boolean;
  /** @nullable */
  calendarError?: string | null;
  /** @nullable */
  outlookCalendarError?: string | null;
  /** @nullable */
  teamsError?: string | null;
  /** @nullable */
  googleTasksError?: string | null;
  syncedAt: string;
}

export type SlackChannelType = typeof SlackChannelType[keyof typeof SlackChannelType];


export const SlackChannelType = {
  channel: 'channel',
  dm: 'dm',
} as const;

export interface SlackChannel {
  id: string;
  name: string;
  type: SlackChannelType;
  /** @nullable */
  memberCount?: number | null;
}

export type TeamsChatChatType = typeof TeamsChatChatType[keyof typeof TeamsChatChatType];


export const TeamsChatChatType = {
  oneOnOne: 'oneOnOne',
  group: 'group',
  meeting: 'meeting',
} as const;

export interface TeamsChat {
  id: string;
  /** @nullable */
  topic: string | null;
  chatType: TeamsChatChatType;
  memberNames: string[];
  /**
     * ISO timestamp of the last message this user has read in the chat (viewpoint.lastMessageReadDateTime). Null when Graph returns no viewpoint.
     * @nullable
     */
  lastReadAt: string | null;
}

export interface TeamsTeam {
  id: string;
  displayName: string;
}

export interface TeamsChannel {
  id: string;
  displayName: string;
}

export interface OutOfOffice {
  active: boolean;
  message?: string;
  /** When the auto-reply window ends, if scheduled. */
  until?: string;
}

export type PresenceInfoAvailability = typeof PresenceInfoAvailability[keyof typeof PresenceInfoAvailability];


export const PresenceInfoAvailability = {
  available: 'available',
  busy: 'busy',
  away: 'away',
  dnd: 'dnd',
  offline: 'offline',
  unknown: 'unknown',
} as const;

/**
 * Volatile presence for a Slack/Teams user, normalized across both providers. Never persisted; served from a short-lived cache.
 */
export interface PresenceInfo {
  availability: PresenceInfoAvailability;
  /** Teams-only richer detail ("InAMeeting", "InACall", "Presenting"). */
  activity?: string;
  statusText?: string;
  /** Slack-only status emoji shortcode. */
  statusEmoji?: string;
  outOfOffice?: OutOfOffice;
  dnd?: boolean;
  fetchedAt: string;
}

/**
 * Map of provider user id → presence. Ids absent from the map had no available presence (render as "unknown").
 */
export interface PresenceMap {[key: string]: PresenceInfo}

export type ContactCardSource = typeof ContactCardSource[keyof typeof ContactCardSource];


export const ContactCardSource = {
  slack: 'slack',
  teams: 'teams',
} as const;

/**
 * Stable directory data for a Slack/Teams user, normalized across both providers.
 */
export interface ContactCard {
  source: ContactCardSource;
  providerUserId: string;
  displayName: string;
  /** Our proxied avatar URL (/api/contacts/{source}/{id}/avatar) — never the raw provider URL. Absent when the person has no photo. */
  avatarUrl?: string;
  email?: string;
  jobTitle?: string;
  department?: string;
  phone?: string;
}

export interface Rule {
  id: number;
  description: string;
  isActive: boolean;
  createdAt: string;
}

export interface RuleInput {
  description: string;
}

export interface InboxStats {
  totalUnread: number;
  gmailUnread: number;
  slackUnread: number;
  outlookUnread: number;
  totalMessages: number;
  gmailTotal: number;
  slackTotal: number;
  outlookTotal: number;
}

export interface ReplyBody {
  body: string;
}

export interface EmailReplyBody {
  body: string;
  replyAll?: boolean;
  attachments?: Blob[];
}

export interface ForwardBody {
  to: string;
  /** @nullable */
  comment?: string | null;
  /** The original message's own attachment ids to carry into the forward. Omit entirely to carry all of them (default, matching normal email client behavior) — only send this when one or more has been explicitly removed. */
  keepAttachmentIds?: string[];
  /** Newly added files, merged with any carried originals. */
  attachments?: Blob[];
}

export type RespondInviteBodyResponse = typeof RespondInviteBodyResponse[keyof typeof RespondInviteBodyResponse];


export const RespondInviteBodyResponse = {
  accepted: 'accepted',
  declined: 'declined',
  tentative: 'tentative',
} as const;

export interface RespondInviteBody {
  response: RespondInviteBodyResponse;
}

export type ComposeBodyProvider = typeof ComposeBodyProvider[keyof typeof ComposeBodyProvider];


export const ComposeBodyProvider = {
  gmail: 'gmail',
  outlook: 'outlook',
  slack: 'slack',
  teams: 'teams',
} as const;

/**
 * Required (non-null) when provider is slack (dm|channel) or teams (chat|channel). Null for gmail/outlook.
 * @nullable
 */
export type ComposeBodyKind = typeof ComposeBodyKind[keyof typeof ComposeBodyKind] | null;


export const ComposeBodyKind = {
  dm: 'dm',
  channel: 'channel',
  chat: 'chat',
} as const;

export interface ComposeBody {
  provider: ComposeBodyProvider;
  /** Recipient identifier. For gmail/outlook: an email address. For slack: a channel ID (post to channel) or a user ID (DM), resolved via /api/search. For teams: a chat ID (kind=chat) or a channel ID (kind=channel, paired with teamId). */
  to: string;
  /**
     * Recipient display name. Required (non-null) when provider is slack or teams — used for the cache channel label. Null for gmail/outlook.
     * @nullable
     */
  toName?: string | null;
  /**
     * Required (non-null) when provider is slack (dm|channel) or teams (chat|channel). Null for gmail/outlook.
     * @nullable
     */
  kind?: ComposeBodyKind;
  /**
     * Required (non-null) when provider is teams and kind is channel — the team the channel belongs to. Null otherwise.
     * @nullable
     */
  teamId?: string | null;
  /**
     * Required (non-null) when provider is gmail or outlook. Null for slack/teams.
     * @nullable
     */
  subject?: string | null;
  /**
     * Optional Cc email addresses. gmail/outlook only, ignored otherwise.
     * @nullable
     */
  cc?: string[] | null;
  /**
     * Optional Bcc email addresses. gmail/outlook only, ignored otherwise.
     * @nullable
     */
  bcc?: string[] | null;
  body: string;
  /**
     * When sending from an existing autosaved draft, its id — deleted server-side once the send succeeds. Omit/null for a message composed from scratch.
     * @nullable
     */
  draftId?: number | null;
  /** gmail/outlook only, ignored otherwise. */
  attachments?: Blob[];
}

export type DraftProvider = typeof DraftProvider[keyof typeof DraftProvider];


export const DraftProvider = {
  gmail: 'gmail',
  outlook: 'outlook',
} as const;

export interface Draft {
  id: number;
  provider: DraftProvider;
  to: string;
  subject: string;
  cc: string[];
  bcc: string[];
  body: string;
  createdAt: string;
  updatedAt: string;
}

export type CreateDraftRequestProvider = typeof CreateDraftRequestProvider[keyof typeof CreateDraftRequestProvider];


export const CreateDraftRequestProvider = {
  gmail: 'gmail',
  outlook: 'outlook',
} as const;

export interface CreateDraftRequest {
  provider: CreateDraftRequestProvider;
  to?: string;
  subject?: string;
  cc?: string[];
  bcc?: string[];
  body?: string;
}

export type UpdateDraftRequestProvider = typeof UpdateDraftRequestProvider[keyof typeof UpdateDraftRequestProvider];


export const UpdateDraftRequestProvider = {
  gmail: 'gmail',
  outlook: 'outlook',
} as const;

export interface UpdateDraftRequest {
  provider?: UpdateDraftRequestProvider;
  to?: string;
  subject?: string;
  cc?: string[];
  bcc?: string[];
  body?: string;
}

export interface OkResult {
  ok: boolean;
}

export interface ApiError {
  error: string;
}

/**
 * Which plan they're interested in — "basic", "pro", "premium", or "unsure". Omit if not asked.
 */
export type JoinWaitlistRequestInterestedPlan = typeof JoinWaitlistRequestInterestedPlan[keyof typeof JoinWaitlistRequestInterestedPlan];


export const JoinWaitlistRequestInterestedPlan = {
  basic: 'basic',
  pro: 'pro',
  premium: 'premium',
  unsure: 'unsure',
} as const;

export interface JoinWaitlistRequest {
  name: string;
  email: string;
  /** Which plan they're interested in — "basic", "pro", "premium", or "unsure". Omit if not asked. */
  interestedPlan?: JoinWaitlistRequestInterestedPlan;
}

export interface WaitlistSignup {
  id: number;
  name: string;
  email: string;
  /** @nullable */
  interestedPlan: string | null;
  createdAt: string;
}

export interface RequestConnectorRequest {
  name: string;
  email: string;
  /** The tool or service they'd like Assistio to connect to. */
  toolName: string;
  /** Optional extra context — not required. */
  comment?: string;
}

export interface ConnectorRequest {
  id: number;
  name: string;
  email: string;
  toolName: string;
  /** @nullable */
  comment: string | null;
  createdAt: string;
}

/**
 * Whether this is a bug report, a feature request, or a general enquiry.
 */
export type SubmitFeedbackRequestType = typeof SubmitFeedbackRequestType[keyof typeof SubmitFeedbackRequestType];


export const SubmitFeedbackRequestType = {
  bug: 'bug',
  feature: 'feature',
  general: 'general',
} as const;

export interface SubmitFeedbackRequest {
  /** Whether this is a bug report, a feature request, or a general enquiry. */
  type: SubmitFeedbackRequestType;
  /** As much detail as possible about the bug, request, or enquiry. */
  details: string;
  name: string;
  email: string;
}

export type FeedbackType = typeof FeedbackType[keyof typeof FeedbackType];


export const FeedbackType = {
  bug: 'bug',
  feature: 'feature',
  general: 'general',
} as const;

export interface Feedback {
  id: number;
  type: FeedbackType;
  details: string;
  name: string;
  email: string;
  createdAt: string;
}

export interface AnthropicConversation {
  id: number;
  title: string;
  createdAt: string;
}

export interface AnthropicMessage {
  id: number;
  conversationId: number;
  role: string;
  content: string;
  createdAt: string;
}

export interface AnthropicConversationInput {
  title: string;
}

export interface AnthropicMessageInput {
  content: string;
}

export interface AnthropicConversationWithMessages {
  id: number;
  title: string;
  createdAt: string;
  messages: AnthropicMessage[];
}

export interface AnthropicError {
  error: string;
}

export interface DateRange {
  from: string;
  to: string;
}

export interface StatusBreakdownEntry {
  /** One of "todo" | "in_progress" | "blocked" | "done". */
  status: string;
  count: number;
}

export interface PriorityBreakdownEntry {
  /**
     * References TaskTag. Null represents tasks with no priority tag set.
     * @nullable
     */
  tagId: number | null;
  name: string;
  color: string;
  count: number;
}

export interface TrendPoint {
  /** Start of the bucket (day, week, or month depending on range length). */
  bucket: string;
  count: number;
}

export interface FocusTimeTrendPoint {
  bucket: string;
  hours: number;
  /** Number of scheduled focus sessions ("meetings") starting in this bucket. */
  count: number;
}

export interface FocusTimeStats {
  totalHours: number;
  /** Number of task_time_blocks starting within the period. */
  sessionCount: number;
  trend: FocusTimeTrendPoint[];
}

export interface PeriodStats {
  range: DateRange;
  tasksCreated: number;
  /** Tasks currently "done" whose completedAt falls in this period. */
  tasksCompleted: number;
  /** tasksCompleted / tasksCreated. 0 when tasksCreated is 0. */
  completionRate: number;
  /** Tasks whose dueDate fell within this period and were not done by dueDate. */
  missedDeadlines: number;
  statusBreakdown: StatusBreakdownEntry[];
  priorityBreakdown: PriorityBreakdownEntry[];
  createdTrend: TrendPoint[];
  completedTrend: TrendPoint[];
  focusTime: FocusTimeStats;
}

export interface AnalyticsResponse {
  current: PeriodStats;
  compare: PeriodStats | null;
  /** Tasks not done with a dueDate before now, evaluated live (not period-bound). */
  overdueNow: number;
}

export type GetMessagesParams = {
/**
 * Filter by source
 */
source?: GetMessagesSource;
limit?: number;
offset?: number;
isRead?: boolean;
/**
 * Filter to one folder by its folderKey (e.g. "domain:acme.com" or "slack_channel:general"). When set, returns that folder's full message history including archived messages, and ignores limit/offset.
 */
folder?: string;
/**
 * Include archived messages in the main (non-folder) list. Always true when `folder` is set.
 */
includeArchived?: boolean;
};

export type GetMessagesSource = typeof GetMessagesSource[keyof typeof GetMessagesSource];


export const GetMessagesSource = {
  all: 'all',
  gmail: 'gmail',
  outlook: 'outlook',
  slack: 'slack',
  teams: 'teams',
  jira: 'jira',
  native: 'native',
} as const;

export type SendNativeChatMessage200 = {
  ok: boolean;
  message: UnifiedMessage;
};

export type GetPresenceParams = {
source: GetPresenceSource;
/**
 * Comma-separated provider user ids (max 200).
 */
ids: string;
};

export type GetPresenceSource = typeof GetPresenceSource[keyof typeof GetPresenceSource];


export const GetPresenceSource = {
  slack: 'slack',
  teams: 'teams',
} as const;

export type GetContactsParams = {
source: GetContactsSource;
/**
 * Comma-separated provider user ids (max 200).
 */
ids: string;
};

export type GetContactsSource = typeof GetContactsSource[keyof typeof GetContactsSource];


export const GetContactsSource = {
  slack: 'slack',
  teams: 'teams',
} as const;

export type GetTaskSummaryParams = {
listId?: number;
};

export type GetAnalyticsParams = {
/**
 * Inclusive start of the primary date range (ISO date or date-time).
 */
from: string;
/**
 * Inclusive end of the primary date range (ISO date or date-time).
 */
to: string;
/**
 * How to compute the comparison period. Defaults to "previous_period" (same duration, immediately preceding `from`). "custom" requires compareFrom/compareTo.
 */
compare?: GetAnalyticsCompare;
/**
 * Required when compare=custom.
 */
compareFrom?: string;
/**
 * Required when compare=custom.
 */
compareTo?: string;
};

export type GetAnalyticsCompare = typeof GetAnalyticsCompare[keyof typeof GetAnalyticsCompare];


export const GetAnalyticsCompare = {
  none: 'none',
  previous_period: 'previous_period',
  previous_week: 'previous_week',
  previous_month: 'previous_month',
  previous_year: 'previous_year',
  custom: 'custom',
} as const;

